// File: src/pages/msme/SAKSHAMFAQ.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const faqData = {
  "MSME Owner": [
    // Negative
    {
      sentiment: "negative",
      question: "Do I really have the time to read all this information?",
      answer:
        "You don’t need to read everything at once. The platform provides short, actionable insights, so you can focus on what matters to grow your business. Even 5 minutes a day can help you make smarter decisions.",
    },
    {
      sentiment: "negative",
      question: "Why should I even bother reading this?",
      answer:
        "Every insight is designed to save you time, money, and effort. Following our guidance can help you get loans faster, reduce business risks, and increase revenue. Think of it as a personal business coach in your pocket.",
    },
    {
      sentiment: "negative",
      question: "How can someone like me, who is illiterate or semi-literate, use this platform?",
      answer:
        "The platform is visual, intuitive, and voice-enabled. You can navigate it easily with icons, images, and spoken instructions—no need to read long texts. Many micro-entrepreneurs are already benefiting from it without literacy barriers.",
    },
    {
      sentiment: "negative",
      question: "If Google is available, why should I use SAKSHAM MSME?",
      answer:
        "Google gives generic information; SAKSHAM MSME gives personalized, actionable guidance based on your business data. It helps you track, plan, and grow, not just search.",
    },
    {
      sentiment: "negative",
      question: "AI tools are everywhere, so why do I need this platform?",
      answer:
        "Unlike generic AI, our platform is specifically built for MSMEs, considering Indian business realities, government schemes, and financial options. It’s your AI assistant tailored for your growth, not just another chatbot.",
    },
    {
      sentiment: "negative",
      question: "Will this really help me grow my business, or is it just another gimmick?",
      answer:
        "Thousands of MSMEs using SAKSHAM MSME have already increased revenue, reduced costs, and accessed better financing. Our AI guides you step by step, so growth is measurable, not hypothetical.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "What kind of support can I get from this platform?",
      answer:
        "You can get AI-driven insights, step-by-step business improvement plans, and access to expert advisors when needed.",
    },
    {
      sentiment: "neutral",
      question: "Is there a mobile app or only a web version?",
      answer:
        "Both! The mobile app is optimized for small screens and works on low-end smartphones. The web version is for deeper analysis.",
    },
    {
      sentiment: "neutral",
      question: "Can I track my progress month by month?",
      answer:
        "Absolutely. The dashboard shows real-time growth metrics, loan application status, and business improvement trends.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "How will this platform help me increase my revenue?",
      answer:
        "It identifies gaps in sales, suggests marketing improvements, and recommends cost-saving measures. Many users see revenue growth within 3-6 months.",
    },
    {
      sentiment: "positive",
      question: "Can I get customized suggestions for my business?",
      answer:
        "Yes, the AI analyzes your business size, sector, location, and financial health to give personalized guidance.",
    },
    {
      sentiment: "positive",
      question: "Will this help me get loans or better access to banks?",
      answer:
        "It prepares your documents, predicts creditworthiness, and suggests the right banks, making loan approvals faster.",
    },
    {
      sentiment: "positive",
      question: "Can I learn modern business practices without attending a formal class?",
      answer:
        "Definitely. The platform provides bite-sized lessons, video tutorials, and AI tips, so learning is practical and fast.",
    },
  ],

  Bank: [
    // Negative
    {
      sentiment: "negative",
      question: "How can we trust the data provided by MSME owners?",
      answer:
        "The platform uses AI verification, cross-checks government databases, and tracks historical patterns, ensuring reliable data for decision-making.",
    },
    {
      sentiment: "negative",
      question: "Will this platform really reduce our risk, or is it just extra paperwork?",
      answer:
        "SAKSHAM MSME reduces risk by predicting potential defaults, highlighting warning signs, and recommending loan limits, so your risk decisions are data-driven, not guesswork.",
    },
    {
      sentiment: "negative",
      question: "If we already have a risk assessment system, why do we need this?",
      answer:
        "Our platform enhances your existing system with AI insights, benchmarking against thousands of MSMEs, making your risk assessment faster, smarter, and more accurate.",
    },
    {
      sentiment: "negative",
      question: "Is this platform secure enough for sensitive financial information?",
      answer:
        "Absolutely. It uses bank-grade encryption, multi-factor authentication, and regular security audits.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Can this platform integrate with our existing banking systems?",
      answer:
        "APIs and secure data connectors allow smooth integration without disrupting current workflows.",
    },
    {
      sentiment: "neutral",
      question: "How often is the data updated on the platform?",
      answer: "Data updates daily, with alerts for critical changes.",
    },
    {
      sentiment: "neutral",
      question: "Can we generate reports for multiple borrowers at once?",
      answer:
        "Yes, the platform has bulk reporting features, exportable to Excel or PDF.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Can this help us identify promising MSMEs faster?",
      answer:
        "The AI highlights high-potential MSMEs based on performance, sector trends, and financial health.",
    },
    {
      sentiment: "positive",
      question: "Will we get AI-based recommendations for loan approvals?",
      answer:
        "Yes, it provides suggested credit limits, risk scores, and actionable recommendations.",
    },
    {
      sentiment: "positive",
      question: "Can it help reduce non-performing assets (NPAs)?",
      answer:
        "Early warning signals, recovery suggestions, and tracking tools help minimize NPAs over time.",
    },
  ],

  ARC: [
    // Negative
    {
      sentiment: "negative",
      question: "How reliable is the MSME data for debt recovery purposes?",
      answer:
        "The platform uses verified financial records, AI pattern detection, and cross-referencing with government and banking databases to ensure high data reliability for recovery decisions.",
    },
    {
      sentiment: "negative",
      question: "Will this platform really improve recoveries, or just create more noise?",
      answer:
        "It focuses on actionable insights, highlighting accounts with recovery potential first, so your efforts are efficient and targeted, not wasted.",
    },
    {
      sentiment: "negative",
      question: "Can we depend on AI predictions for making financial decisions?",
      answer:
        "Yes, our AI provides probabilistic recommendations based on historical trends, but final decisions remain with your experts—so AI augments, not replaces, your judgment.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Does this platform provide historical financial records of MSMEs?",
      answer:
        "Yes, it maintains year-on-year records, bank transactions, loan history, and repayment patterns, all accessible through the dashboard.",
    },
    {
      sentiment: "neutral",
      question: "Can we customize our dashboards for monitoring NPA accounts?",
      answer:
        "Absolutely. You can set filters, alerts, and key metrics based on your operational needs.",
    },
    {
      sentiment: "neutral",
      question: "Is there a way to flag risky MSMEs automatically?",
      answer: "Yes, the AI assigns a risk score to each MSME and flags accounts requiring attention.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Can we use this platform to speed up asset resolution?",
      answer:
        "By prioritizing accounts, tracking recovery progress, and receiving actionable insights, ARCs can resolve assets faster.",
    },
    {
      sentiment: "positive",
      question: "Will it provide actionable insights for recovery strategies?",
      answer:
        "AI suggests optimal recovery methods, payment plans, and legal intervention points.",
    },
    {
      sentiment: "positive",
      question: "Can we monitor MSME growth to prevent future defaults?",
      answer:
        "The platform tracks business health in real-time, helping you identify potential risks before they escalate.",
    },
  ],

  Government: [
    // Negative
    {
      sentiment: "negative",
      question:
        "Will this platform truly reflect the ground reality, or just paper statistics?",
      answer:
        "The platform collects real-time data directly from MSMEs, validated by AI and cross-checked with government records—ensuring ground-level insights, not just statistics.",
    },
    {
      sentiment: "negative",
      question:
        "How can we ensure that 100% of MSMEs adopt this platform?",
      answer:
        "Adoption is encouraged via localized training, mobile-friendly access, incentives, and integration with existing government schemes.",
    },
    {
      sentiment: "negative",
      question:
        "Is this just another tech initiative that may not reach rural areas?",
      answer:
        "No. The platform is lightweight, offline-compatible, and voice-enabled, designed specifically for rural MSMEs and low-connectivity areas.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Can this platform provide consolidated reports for policy planning?",
      answer:
        "Policymakers can generate state-wise, sector-wise, and scheme-wise reports instantly.",
    },
    {
      sentiment: "neutral",
      question: "Does it track MSME growth and employment generation?",
      answer:
        "The dashboard shows growth metrics, employment numbers, and scheme impact analytics.",
    },
    {
      sentiment: "neutral",
      question: "Can the platform help in evaluating government schemes?",
      answer:
        "Yes, you can measure adoption, effectiveness, and ROI of various schemes.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Can we use this platform to boost MSME productivity and employment?",
      answer:
        "Insights from the platform help identify high-potential sectors and regions, allowing targeted interventions to maximize growth and employment.",
    },
    {
      sentiment: "positive",
      question: "Will AI help us identify sectors needing government intervention?",
      answer:
        "AI flags underperforming sectors, emerging risks, and policy gaps.",
    },
    {
      sentiment: "positive",
      question: "Can we design better schemes using insights from this platform?",
      answer:
        "Data-driven insights enable smarter policy-making and better resource allocation.",
    },
  ],

  Advisor: [
    // Negative
    {
      sentiment: "negative",
      question: "Will this platform replace traditional advisory services?",
      answer:
        "No. It enhances advisory services, allowing advisors to focus on strategic decision-making, while the AI handles routine analysis.",
    },
    {
      sentiment: "negative",
      question: "How accurate is the AI advice for small businesses?",
      answer:
        "The AI is trained on thousands of MSMEs, sector benchmarks, and local market data, providing highly accurate, actionable guidance.",
    },
    {
      sentiment: "negative",
      question: "Can we rely on this platform for sensitive strategic decisions?",
      answer:
        "Yes, but AI is advisory only. You can combine its insights with your expertise for better-informed decisions.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Can we provide recommendations directly on the platform?",
      answer:
        "Advisors can add suggestions, monitor adoption, and track implementation.",
    },
    {
      sentiment: "neutral",
      question: "Does it allow monitoring multiple MSMEs at once?",
      answer:
        "Absolutely. You can manage portfolios of MSMEs efficiently through customizable dashboards.",
    },
    {
      sentiment: "neutral",
      question: "Can we access client performance dashboards easily?",
      answer:
        "Yes, client dashboards are intuitive, mobile-friendly, and filterable for quick analysis.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Can this platform make our advisory services more impactful?",
      answer:
        "AI identifies bottlenecks, opportunities, and sector trends, making your advice more targeted and results-driven.",
    },
    {
      sentiment: "positive",
      question: "Will AI help us quickly identify business bottlenecks?",
      answer:
        "The platform highlights critical areas needing attention in real-time.",
    },
    {
      sentiment: "positive",
      question: "Can we expand our advisory reach beyond local regions?",
      answer:
        "Digital dashboards and AI analysis let you serve clients nationwide without physical visits.",
    },
  ],

  Auditor: [
    // Negative
    {
      sentiment: "negative",
      question: "How can we verify the authenticity of data submitted by MSMEs?",
      answer:
        "The platform uses cross-verification with bank statements, GST records, and AI anomaly detection to ensure authenticity.",
    },
    {
      sentiment: "negative",
      question: "Will this platform comply with regulatory requirements?",
      answer:
        "Yes. It is designed to adhere to RBI, GST, and government compliance standards.",
    },
    {
      sentiment: "negative",
      question: "Can it handle audit trails for all financial transactions?",
      answer:
        "Absolutely. Every action is time-stamped, logged, and auditable.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Does it support exporting data for auditing purposes?",
      answer:
        "Yes, all data can be exported in Excel, CSV, or PDF formats for external audits.",
    },
    {
      sentiment: "neutral",
      question: "Can we track compliance metrics of MSMEs?",
      answer:
        "Compliance dashboards show real-time GST filings, loan compliance, and statutory adherence.",
    },
    {
      sentiment: "neutral",
      question: "How frequently is the data updated?",
      answer: "Data updates daily, ensuring auditors see the most current information.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Can we reduce manual auditing efforts?",
      answer:
        "Automated reports and AI anomaly detection save hours of manual effort.",
    },
    {
      sentiment: "positive",
      question: "Will it help identify discrepancies faster?",
      answer:
        "AI flags potential errors, missing filings, and suspicious transactions immediately.",
    },
    {
      sentiment: "positive",
      question: "Can it generate compliance reports automatically?",
      answer:
        "Yes, you can schedule automated reports for internal or regulatory purposes.",
    },
  ],
};

// Color coding for sentiment
const sentimentColors = {
  negative: "text-orange-400",
  neutral: "text-gray-400",
  positive: "text-green-400",
};

export default function SAKSHAMFAQ() {
  const navigate = useNavigate();
  const stakeholders = Object.keys(faqData);
  const [selectedStakeholder, setSelectedStakeholder] = useState(
    stakeholders[0]
  );
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8">
      <h1 className="text-lg sm:text-xl font-bold mb-6 text-center">
        Think5Whys SAKSHAM MSME FAQ
      </h1>

      {/* Stakeholder Tabs */}
      <div className="grid grid-cols-4 lg:grid-cols-7 gap-1 mb-2 place-items-center">
        {stakeholders.map((stakeholder) => (
            <button
            key={stakeholder}
            onClick={() => {
                setSelectedStakeholder(stakeholder);
                setExpandedIndex(null);
            }}
            className={`px-4 py-2 rounded ${
                selectedStakeholder === stakeholder
                ? "bg-yellow-600 text-gray-900"
                : "bg-gray-700 hover:bg-gray-600"
            } font-semibold text-sm sm:text-base`}
            >
            {stakeholder}
            </button>
        ))}
        <button
          onClick={() => navigate("/msme-whysakshammsme")}
          className="px-4 py-2 rounded bg-yellow-600 text-gray-900"
        >
          About
        </button>
      </div>


      {/* FAQ List */}
      <div className="max-w-4xl mx-auto">
        {faqData[selectedStakeholder].map((faq, index) => (
          <div
            key={index}
            className="border-b border-gray-700 py-3 cursor-pointer"
            onClick={() =>
              setExpandedIndex(expandedIndex === index ? null : index)
            }
          >
            <div className="flex justify-between items-center">
              <h3
                className={`font-medium ${sentimentColors[faq.sentiment]}`}
              >
                {faq.question}
              </h3>
              <span className="text-gray-400 text-xl">
                {expandedIndex === index ? "-" : "+"}
              </span>
            </div>
            {expandedIndex === index && (
              <p className="mt-2 text-gray-300">{faq.answer}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
